// The editor's documents, as the player reads them.
//
// A product being edited holds content keys where the storefront holds values:
// `subtitle.web.text` is `Product-pages.3xrn62.subtitle.web.text-<pid>`, and a
// background image is a key whose value is a path inside the media library.
// The API resolves them for a reader; here they are resolved from what the
// editor has already loaded, so the preview plays the same product the
// storefront would — text as text, audio that sounds.
import { urljoin } from "../shared/useProductEditor"

const idOf = (value) => (value && typeof value === "object" ? String(value._id) : value ? String(value) : undefined)

export function makeResolver({ text, language, defaultLanguage, mediaRoot, durations = {} }) {
  const value = (raw) => {
    const id = idOf(raw)
    if (!id) return ""
    // a key resolves; anything else is already a value
    const found = text(id, language) || text(id, defaultLanguage) || ""
    return found || (id.startsWith("Product-") ? "" : id)
  }
  const media = (raw) => {
    const found = value(raw)
    if (!found) return ""
    return /^https?:/i.test(found) ? found : urljoin(mediaRoot, found)
  }
  // how long a voice-over is. The player is told by the API
  // (subtitleAudioDuration); the editor asks the media library the same way
  // the page form does (book {mediaDurations}), so a scene on the timeline is
  // as long here as it will be for a reader.
  const duration = (raw) => Number(durations[value(raw)]) || 0

  return { value, media, duration }
}

/** One page with its keys turned into text and urls. */
export function resolvePage(page, resolver) {
  if (!page) return page
  const layouts = (obj, map) => {
    if (!obj) return obj
    const out = {}
    for (const [name, layer] of Object.entries(obj)) out[name] = layer ? map(layer) : layer
    return out
  }

  return {
    ...page,
    // an unset title resolves to nothing; falling back to the key would put
    // "Product-pages.1u237io.title-…" on screen as if it were a name
    title: resolver.value(page.title),
    background: layouts(page.background, (layer) => ({ ...layer, image: resolver.media(layer.image) })),
    subtitle: layouts(page.subtitle, (layer) => ({
      ...layer,
      text: resolver.value(layer.text),
      audio: resolver.media(layer.audio),
      audioDuration: Number(layer.audioDuration) || resolver.duration(layer.audio),
    })),
    music: resolver.media(page.music) || undefined,
    musicDuration: resolver.duration(page.music),
    sfx: resolver.media(page.sfx) || undefined,
    // how long the effect is, so the timeline can draw the one shot it is
    // (a looping effect runs as long as its scene — playback.lua)
    sfxDuration: resolver.duration(page.sfx),
    triggerAreas: (page.triggerAreas || []).map((trigger) => ({ ...trigger, audio: resolver.media(trigger.audio) })),
  }
}

/** The product with every page resolved — what the preview engine is given. */
export function resolveProduct(product, resolver) {
  if (!product) return product
  return { ...product, pages: (product.pages || []).map((page) => resolvePage(page, resolver)) }
}
